import { createContext, useState } from "react";

type AddSessionsArgs = {
  entityType: UploadSessionEntityType;
  entityId: number | string;
  uploadIds: string[];
};

export type EntityUploadSessions = {
  [key in UploadSessionEntityType]?: {
    [entityId: string]: string[];
  };
};

export enum UploadSessionEntityType {
  Assets = "Assets",
}

export const EntityUploadSessionsContext =
  createContext<ReturnType<typeof useProvider>>(null);

export const EntityUploadSessionsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const value = useProvider();
  return (
    <EntityUploadSessionsContext value={value}>
      {children}
    </EntityUploadSessionsContext>
  );
};

function useProvider() {
  const [sessions, setSessions] = useState<
    {
      entityType: UploadSessionEntityType;
      entityId: string | number;
      id: string;
    }[]
  >([]);

  const sessionsTree = sessions.reduce((grouped, x) => {
    const entities = grouped[x.entityType] ?? (grouped[x.entityType] = {});
    const ids = entities[x.entityId] ?? (entities[x.entityId] = []);
    ids.push(x.id);
    return grouped;
  }, {} as EntityUploadSessions);

  const addSessions = (args: AddSessionsArgs) => {
    if (!args) return;
    const { entityType, entityId, uploadIds } = args;
    setSessions((state) => [
      ...state,
      ...uploadIds.map((id) => ({ entityType, entityId, id })),
    ]);
  };

  function changeSessionId(id: string, newId: string) {
    if (id == null || newId == null || id == newId) return;
    setSessions((state) =>
      state.map((x) => (x.id == id ? { ...x, id: newId } : x)),
    );
  }

  return {
    sessions: sessionsTree,
    addSessions,
    changeSessionId,
  };
}
